import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Users,
  ChevronDown,
  UserPlus,
  List,
  Upload,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Briefcase,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const EMPLOYEE_STATUSES = [
  { code: "Active", desc: "Employee is currently working in the organisation.", tint: "ic-green" },
  { code: "Inactive", desc: "Employee record exists but is not currently active.", tint: "ic-slate" },
  { code: "On Leave", desc: "Employee is on an approved leave of absence.", tint: "ic-amber" },
  { code: "Terminated", desc: "Employee has been terminated from the organisation.", tint: "ic-rose" },
];

const EMPLOYEE_TYPES = [
  "Permanent", "Temporary", "Contract", "Probation", "Intern",
  "Trainee", "Consultant", "Part-Time", "Fixed Term", "Apprentice",
  "Daily Wage", "Seasonal", "Outsourced",
];

const CREATION_STEPS = [
  { num: 1, label: "Basic Details", desc: "Name, email, phone, gender, date of birth, employee type." },
  { num: 2, label: "Organisation", desc: "Department, designation, location, and system access role." },
  { num: 3, label: "Compensation", desc: "Date of joining, annual CTC, and pay frequency." },
  { num: 4, label: "Banking (Optional)", desc: "IFSC code, bank name, account number. Can be skipped." },
  { num: 5, label: "Statutory (Optional)", desc: "UAN, PAN, Aadhaar, PF, ESI, and applicability flags. Can be skipped." },
];

const FAQ_DATA = [
  {
    q: "What is the Employee Module?",
    a: "The Employee Module in BrixiGo lets you add, view, edit, and manage employee records. It is the central hub for all employee information including personal details, organisation assignment, compensation, banking, and statutory details.",
  },
  {
    q: "How do I add a new employee?",
    a: "Go to Employees and click the 'Add Employee' tab. Fill in the 5-step wizard: Basic Details, Organisation, Compensation, Banking (optional), and Statutory (optional). The employee code is auto-generated based on your organisation's prefix. Click Save to create the record.",
  },
  {
    q: "How do I view or search for an employee?",
    a: "Go to Employees > All Employees. Use the search bar to search by name, employee code, email, department, or designation. You can also filter by employee type (Permanent, Temporary, Contract, Intern) and toggle 'Include Inactive' to show inactive employees.",
  },
  {
    q: "How do I edit an employee's information?",
    a: "From the employee list, click on an employee row to open the preview drawer, then click 'Open full record'. Alternatively, click the Edit button in the Actions column. This opens the same 5-step wizard in edit mode where you can update any field.",
  },
  {
    q: "How do I import employees in bulk?",
    a: "Go to Employees > Bulk Import. Download the sample template (CSV or XLSX), fill in your employee data, then upload the completed file. The system will auto-map columns, let you review mappings, validate the data, and show any errors before importing.",
  },
  {
    q: "What file formats are supported for bulk import?",
    a: "BrixiGo supports .csv and .xlsx files. The template includes columns for all 5 steps (personal, organisation, compensation, banking, statutory). Banking and statutory columns are optional. Use YYYY-MM-DD format for dates.",
  },
  {
    q: "How does the Employee Module relate to Payroll?",
    a: "Each employee's Annual CTC is used as the base for salary structure assignment. After creating an employee, you can assign a salary structure (pay template) which breaks the CTC into salary components. The employee's banking details are used for salary disbursement, and statutory flags (PF, ESI, PT) determine payroll deductions.",
  },
  {
    q: "Can I export the employee list?",
    a: "Yes. From the All Employees view, click the Export button and choose CSV or Excel (.xlsx). The exported file includes all employee information visible in the table.",
  },
  {
    q: "What is the employee code and can I customise it?",
    a: "The employee code is a unique identifier auto-generated with your organisation's prefix (e.g. BRX001). You can configure the prefix in Settings > Employee Setup. The number auto-increments for each new employee.",
  },
  {
    q: "How do I delete an employee?",
    a: "From the employee list, click the Delete button in the Actions column. A confirmation dialog will appear. Once confirmed, the employee record is permanently deleted.",
  },
];

const ERROR_SOLUTIONS = [
  {
    error: "Employee Name is required",
    cause: "The employee name field was left blank in Step 1.",
    fix: "Enter the employee's full name in the Employee Name field before proceeding.",
  },
  {
    error: "Work Email is required",
    cause: "No work email was provided in Step 1.",
    fix: "Enter a valid work email address. This is used for communication and system login.",
  },
  {
    error: "Salary must be greater than zero",
    cause: "The Annual CTC field in Step 3 is empty or set to zero.",
    fix: "Enter the employee's annual salary (CTC) as a number greater than zero.",
  },
  {
    error: "Please select a department / designation / location",
    cause: "A required organisation field was not selected in Step 2.",
    fix: "Select a department, designation, and location from the dropdowns. If none exist, use the '+' button to create one inline.",
  },
  {
    error: "Cannot add employees — Your account is not linked to an organisation",
    cause: "Your user account is not associated with any organisation in BrixiGo.",
    fix: "Complete the Organisation Setup in Settings before adding employees. Contact your admin if you believe this is an error.",
  },
  {
    error: "Account numbers do not match",
    cause: "The 'Account Number' and 'Confirm Account Number' fields in Step 4 do not match.",
    fix: "Re-enter the account number carefully in both fields. They must be identical.",
  },
  {
    error: "IFSC format looks invalid (e.g. HDFC0001234)",
    cause: "The IFSC code entered does not match the standard 11-character format.",
    fix: "Enter a valid 11-character IFSC code (4 letters + 0 + 6 alphanumeric characters, e.g. HDFC0001234).",
  },
  {
    error: "UAN must be exactly 12 digits (numbers only)",
    cause: "The UAN field in Step 5 does not contain exactly 12 numeric digits.",
    fix: "Enter the employee's 12-digit Universal Account Number (EPFO UAN) using numbers only.",
  },
  {
    error: "PAN must match format AAAAA9999A",
    cause: "The PAN entered does not match the standard Indian PAN format.",
    fix: "Enter a valid 10-character PAN: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F).",
  },
  {
    error: "Failed to load employees. Please refresh.",
    cause: "The employee list could not be retrieved from the server.",
    fix: "Refresh the page. If the issue persists, check your internet connection or contact support.",
  },
  {
    error: "employee_code column missing (bulk import)",
    cause: "This error appears during bulk upload but is informational — employee codes are auto-generated.",
    fix: "You do not need an employee_code column in your import file. The system auto-generates codes using your organisation prefix.",
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function EmployeeHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const lc = (s) => s.toLowerCase();

  const filteredFaqs = search.trim()
    ? FAQ_DATA.filter((f) => lc(f.q).includes(lc(search)) || lc(f.a).includes(lc(search)))
    : FAQ_DATA;

  const filteredErrors = search.trim()
    ? ERROR_SOLUTIONS.filter(
        (e) => lc(e.error).includes(lc(search)) || lc(e.cause).includes(lc(search)) || lc(e.fix).includes(lc(search))
      )
    : ERROR_SOLUTIONS;

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-violet">
              <Users size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Employee Module</h1>
              <p className="hs-hero-sub">
                Learn how to add, manage, and maintain employee records in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. add employee, bulk import, ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* ── 1. Overview ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Employee Module Overview</h2>
        </div>
        <div className="ah-content-card">
          <p>
            The Employee Module is the central hub for managing your workforce in BrixiGo.
            It allows you to create employee records, organise them by department, designation,
            and location, assign compensation, and link banking and statutory details — all in
            one place.
          </p>
          <p>
            Employee data feeds directly into Payroll (for salary processing), Attendance
            (for tracking), and Leave (for balance management).
          </p>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <UserPlus size={18} className="ic-green" />
              <span>Add employees individually or in bulk</span>
            </div>
            <div className="ah-feature-item">
              <List size={18} className="ic-blue" />
              <span>View, search, and filter employee list</span>
            </div>
            <div className="ah-feature-item">
              <Edit3 size={18} className="ic-amber" />
              <span>Edit employee details anytime</span>
            </div>
            <div className="ah-feature-item">
              <Upload size={18} className="ic-teal" />
              <span>Bulk import via CSV or Excel</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. How It Works — 5 Step Wizard ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <UserPlus size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">How to Add an Employee</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo uses a guided 5-step wizard to create a complete employee record.
            Steps 4 (Banking) and 5 (Statutory) are optional and can be completed later.
          </p>
          <div className="ah-workflow-steps">
            {CREATION_STEPS.map((step, idx) => (
              <div key={step.num} className="ah-workflow-step">
                <div className="ah-workflow-num">{step.num}</div>
                <div className="ah-workflow-body">
                  <div className="ah-workflow-label">{step.label}</div>
                  <div className="ah-workflow-desc">{step.desc}</div>
                </div>
                {idx < CREATION_STEPS.length - 1 && <div className="ah-workflow-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Managing Employees ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <List size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Managing Employees</h2>
        </div>
        <div className="ah-content-card">
          <p>
            The People Hub at <strong>Employees</strong> in the sidebar provides three main tabs:
          </p>

          <div className="ah-manage-grid">
            <div className="ah-manage-card">
              <div className="ah-manage-title">
                <Users size={16} className="ic-blue" />
                Employee Management
              </div>
              <ul className="ah-manage-list">
                <li><strong>All Employees</strong> — View, search, filter, and export the employee list</li>
                <li><strong>Add Employee</strong> — Create a new employee using the 5-step wizard</li>
                <li><strong>Bulk Import</strong> — Upload employees via CSV or Excel template</li>
              </ul>
            </div>

            <div className="ah-manage-card">
              <div className="ah-manage-title">
                <Briefcase size={16} className="ic-amber" />
                Reporting Hierarchy
              </div>
              <ul className="ah-manage-list">
                <li>Assign reporting managers to employees</li>
                <li>View and edit the org hierarchy tree</li>
                <li>See direct reports and manager chains</li>
              </ul>
            </div>

            <div className="ah-manage-card">
              <div className="ah-manage-title">
                <UserPlus size={16} className="ic-teal" />
                Employee Lifecycle
              </div>
              <ul className="ah-manage-list">
                <li>Manage onboarding for new joiners</li>
                <li>Handle offboarding and exit processes</li>
                <li>View exit analytics and audit logs</li>
              </ul>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <p style={{ marginBottom: 8 }}><strong>Employee list features:</strong></p>
            <div className="ah-feature-grid">
              <div className="ah-feature-item">
                <Search size={16} className="ic-blue" />
                <span>Search by name, code, email, department</span>
              </div>
              <div className="ah-feature-item">
                <List size={16} className="ic-amber" />
                <span>Filter by employee type</span>
              </div>
              <div className="ah-feature-item">
                <Upload size={16} className="ic-green" />
                <span>Export to CSV or Excel</span>
              </div>
              <div className="ah-feature-item">
                <Trash2 size={16} className="ic-rose" />
                <span>Delete with confirmation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Employee Information ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-amber" />
          <h2 className="hs-section-title">Employee Information</h2>
        </div>
        <div className="ah-content-card">
          <p>Each employee record in BrixiGo contains the following information, organised by step:</p>

          <div className="ah-info-group">
            <div className="ah-info-heading">Step 1 — Basic Details</div>
            <div className="ah-info-chips">
              {["Employee Code (auto)", "Employee Type", "Employee Name", "Display Name", "Work Email", "Mobile Phone", "Gender", "Date of Birth", "Marital Status", "Father's Name"].map((f) => (
                <span key={f} className="ah-info-chip">{f}</span>
              ))}
            </div>
          </div>

          <div className="ah-info-group">
            <div className="ah-info-heading">Step 2 — Organisation</div>
            <div className="ah-info-chips">
              {["Department", "Designation", "Location", "Business Unit", "System Access Role"].map((f) => (
                <span key={f} className="ah-info-chip">{f}</span>
              ))}
            </div>
          </div>

          <div className="ah-info-group">
            <div className="ah-info-heading">Step 3 — Compensation</div>
            <div className="ah-info-chips">
              {["Date of Joining", "Employee Status", "Annual CTC (INR)", "Pay Frequency (Monthly)"].map((f) => (
                <span key={f} className="ah-info-chip">{f}</span>
              ))}
            </div>
          </div>

          <div className="ah-info-group">
            <div className="ah-info-heading">Step 4 — Banking (Optional)</div>
            <div className="ah-info-chips">
              {["IFSC Code", "Bank Name (auto-filled)", "Branch Name (auto-filled)", "Account Holder Name", "Account Number"].map((f) => (
                <span key={f} className="ah-info-chip">{f}</span>
              ))}
            </div>
          </div>

          <div className="ah-info-group">
            <div className="ah-info-heading">Step 5 — Statutory (Optional)</div>
            <div className="ah-info-chips">
              {["UAN Number", "PAN Number", "Aadhaar Number", "PF Member ID", "ESIC Number", "LWF Number", "Employment Category", "PF / ESI / PT / LWF / Bonus Applicability"].map((f) => (
                <span key={f} className="ah-info-chip">{f}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Employee Statuses & Types ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CheckCircle2 size={20} className="ah-section-icon ic-teal" />
          <h2 className="hs-section-title">Employee Statuses & Types</h2>
        </div>
        <div className="ah-content-card">
          <p><strong>Employee Statuses</strong></p>
          <div className="ah-status-grid" style={{ marginBottom: 20 }}>
            {EMPLOYEE_STATUSES.map((s) => (
              <div key={s.code} className="ah-status-item">
                <div className="ah-status-header">
                  <span className={`ah-status-code ${s.tint}`}>{s.code}</span>
                </div>
                <div className="ah-status-desc">{s.desc}</div>
              </div>
            ))}
          </div>

          <p><strong>Employee Types</strong></p>
          <div className="ah-info-chips">
            {EMPLOYEE_TYPES.map((t) => (
              <span key={t} className="ah-info-chip">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Employee & Payroll ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Briefcase size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Employee & Payroll</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Employee records are the foundation for payroll processing in BrixiGo. Here is how
            they connect:
          </p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Annual CTC</strong> set during employee creation is used as the base for salary structure assignment</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span>After creating an employee, you can <strong>assign a salary structure</strong> (pay template) that breaks the CTC into components</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Primary bank account</strong> is used for salary disbursement during payroll runs</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Statutory flags</strong> (PF, ESI, PT, LWF) determine which deductions apply during payroll</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span>Banking details should be added and verified before processing payroll for an employee</span>
            </div>
            <div className="ah-payroll-item ah-payroll-no">
              <XCircle size={16} />
              <span>Employees without a salary structure assigned will <strong>not</strong> appear in payroll processing</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. FAQs ── */}
      <section className="ah-section" ref={faqRef}>
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="ah-content-card">
          <div className="hs-faq-list">
            {filteredFaqs.length === 0 && (
              <p className="ah-empty">No matching questions found.</p>
            )}
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{faq.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 7. Common Errors ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Common Errors & Solutions</h2>
        </div>
        <div className="ah-content-card">
          {filteredErrors.length === 0 && (
            <p className="ah-empty">No matching errors found.</p>
          )}
          <div className="ah-errors-list">
            {filteredErrors.map((item) => (
              <div key={item.error} className="ah-error-item">
                <div className="ah-error-title">
                  <AlertTriangle size={14} />
                  <code>{item.error}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{item.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
